import { Component, Input, OnChanges } from '@angular/core';
import { OwnerPrices } from 'src/app/core/models/price-list';
import { PriceListService } from 'src/app/core/services/general/price-list.service';
import { ToastService } from 'src/app/core/services/toast-service.service';

/**
 * The non-base prices for ONE record, shown on the screen that owns that record.
 *
 * Tatenda, 2026-10-07: *"everywhere with a USD price needs a ZiG price… if I change price under Purposes it
 * should also change on Price List, and if I change on Price List it should change on Purposes."*
 *
 * That two-way behaviour is not kept in step by this component — it is true because there is only ONE place
 * the money lives. The base price IS the record's own column (edited by the record's own form, as always);
 * every non-base price IS a `currency_prices` row, and this writes those through the very endpoint the
 * central Price List writes. Neither screen holds a copy, so neither can go stale.
 *
 * It renders NOTHING when there is no non-base currency to type — on a single-currency terminal, which is
 * every terminal until ZiG is switched on, no screen grows a new empty section.
 */
@Component({
  selector: 'app-owner-prices',
  templateUrl: './owner-prices.component.html',
  styleUrls: ['./owner-prices.component.scss'],
})
export class OwnerPricesComponent implements OnChanges {
  /** The registry's name for this kind of record: PURPOSE, WAREHOUSE_CONTRACT, … */
  @Input() ownerType!: string;

  /** The record. Absent while a CREATE form has not saved yet — see `awaitingRecord`. */
  @Input() ownerUuid?: string | null;

  /** Heading above the block; the owning screen names it in its own words. */
  @Input() heading = 'Prices in other currencies';

  public prices: OwnerPrices | null = null;
  public loading = false;
  public loadError: string | null = null;

  /** The amount being typed, keyed field|currency. */
  public drafts: { [key: string]: number | null } = {};
  public saving: { [key: string]: boolean } = {};
  public saved: { [key: string]: boolean } = {};
  public cellError: { [key: string]: string | null } = {};

  constructor(private priceListService: PriceListService, private toastService: ToastService) {}

  ngOnChanges(): void {
    this.load();
  }

  /**
   * A record that does not exist yet cannot hold a price row, because a price row is keyed by its uuid.
   * Said plainly rather than rendering inputs that would silently discard what was typed into them.
   */
  get awaitingRecord(): boolean {
    return !this.ownerUuid;
  }

  /** The books a price may be typed in here — never the base, which is the record's own column. */
  get otherBooks(): string[] {
    return (this.prices?.currencies ?? []).filter((c) => c !== this.prices?.base);
  }

  get shows(): boolean {
    return this.otherBooks.length > 0;
  }

  public key(field: string, currency: string): string {
    return `${field}|${currency}`;
  }

  public label(field: string): string {
    return this.prices?.record?.fields?.[field]?.label ?? field;
  }

  /** What the BASE charges for this field — shown beside the box so the two are typed against each other. */
  public baseAmount(field: string): number | null {
    const base = this.prices?.base ?? '';

    return this.prices?.record?.fields?.[field]?.by_currency?.[base]?.amount ?? null;
  }

  public cell(field: string, currency: string) {
    return this.prices?.record?.fields?.[field]?.by_currency?.[currency] ?? null;
  }

  /**
   * A charge the BASE bills for but this book has no price for. Loud on purpose: it does not fall back to
   * the dollar figure, it refuses the charge outright, because this system holds no exchange rate.
   */
  public blocks(field: string, currency: string): boolean {
    const base = this.baseAmount(field);

    return this.cell(field, currency)?.state === 'missing' && base !== null && Number(base) > 0;
  }

  /** A row saved as "this record has no such charge" — bills zero, and is NOT a missing price. */
  public isNoCharge(field: string, currency: string): boolean {
    return this.cell(field, currency)?.state === 'no_value';
  }

  public load(): void {
    this.prices = null;
    this.loadError = null;

    if (this.awaitingRecord || !this.ownerType) {
      return;
    }

    this.loading = true;
    this.priceListService.forOwner(this.ownerType, this.ownerUuid as string).subscribe({
      next: (res) => {
        this.prices = res;
        this.loading = false;
        this.drafts = {};
        for (const field of res.fields) {
          for (const currency of this.otherBooks) {
            this.drafts[this.key(field, currency)] = this.cell(field, currency)?.amount ?? null;
          }
        }
      },
      error: (err) => {
        this.loading = false;
        // A failed LOAD is not "no prices": those are opposite facts and only one of them means somebody
        // has work to do.
        this.loadError = err?.error?.message ?? 'The prices for this record could not be loaded.';
      },
    });
  }

  /**
   * Save the figure typed in the box.
   *
   * A BLANK box is refused (#54). The price book reads a null amount as "this record has no such charge" and
   * bills zero — so a box cleared by accident, or a Save pressed on a box never filled, used to make the charge
   * free in that book. That is a real decision, so it is its own button ({@see saveNoCharge}), exactly as on the
   * central Price List.
   */
  /**
   * Enter saves THIS price and nothing else (#110, 2026-10-26). The box sits inside the record's own form, whose Save is
   * the default button: handled on key-up, Enter had already submitted the whole record (and left the page) on key-down.
   */
  public onEnter(event: Event, field: string, currency: string): void {
    event.preventDefault();
    this.save(field, currency);
  }

  public save(field: string, currency: string): void {
    const key = this.key(field, currency);
    const raw = this.drafts[key];

    if (raw === null || raw === undefined || `${raw}`.trim() === '') {
      this.cellError[key] = `Type a figure, or use "No charge" to record that this record has no such charge in ${currency}.`;
      this.saved[key] = false;
      return;
    }
    const parsed = Number(raw);
    if (!isFinite(parsed) || parsed < 0) {
      this.cellError[key] = 'That is not an amount.';
      this.saved[key] = false;
      return;
    }

    this.write(field, currency, Math.round(parsed * 100) / 100);
  }

  /** Record, deliberately, that this record has no such charge in `currency` — it bills zero there. */
  public saveNoCharge(field: string, currency: string): void {
    this.write(field, currency, null);
  }

  private write(field: string, currency: string, amount: number | null): void {
    const key = this.key(field, currency);

    this.saving[key] = true;
    this.cellError[key] = null;
    this.saved[key] = false;

    this.priceListService
      .set({
        owner_type: this.prices!.owner_type,
        owner_uuid: this.ownerUuid as string,
        currency,
        field,
        // Sent EXPLICITLY, null included: null means "no such charge here" and bills zero, which is not
        // the same as having no price at all (that refuses the charge). Only "No charge" sends it.
        amount,
      })
      .subscribe({
        next: (res) => {
          this.saving[key] = false;
          this.saved[key] = true;
          // A change made while charges exist STARTS NOW (#101): say so, so nobody wonders whether old bills moved.
          this.toastService.showSuccess(res?.effective_from
            ? `${this.label(field)} saved in ${currency}. The new price applies from now; charges already made keep the old one.`
            : `${this.label(field)} saved in ${currency}.`);
          // Re-read rather than patch in place, so this screen shows exactly what the Price List would.
          this.load();
        },
        error: (err) => {
          this.saving[key] = false;
          // VERBATIM: a refused retype names what has to be settled first.
          this.cellError[key] = err?.error?.message ?? 'That price could not be saved.';
        },
      });
  }
}
